import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useGetEngagementsLazyQuery, DdbEngagementType, EngagementFilter } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';

/** Transformed engagement for display in detail sheet lists */
export interface EngagementListItem {
  id: string;
  title: string;
  date: string;
  attendees: string[];
  companies: unknown[];
  from: string;
  preview: string;
  hasAiSummary: boolean;
  hasAiBrief: boolean;
  hasTranscript: boolean;
  contacts: any;
  accounts: any;
  displayName: string;
  description: string;
  startTime: string;
  metadataFrom: any;
  /** Call specifics (direction, outcome, duration, recording…) for CALL engagements. */
  metadata?: unknown;
  endTime?: string;
  link?: string;
}

/** Transform raw GraphQL engagement node into a display-friendly object */
export function transformEngagement(eng: Record<string, unknown>): EngagementListItem {
  return {
    id: (eng.id as string) || '',
    title: (eng.displayName as string) || (eng.subject as string) || 'Untitled',
    date: (eng.startTime as string) || '',
    attendees: (Array.isArray(eng.contacts) ? eng.contacts : []).map((c: Record<string, unknown>) =>
      [
        (c.properties as Record<string, unknown>)?.firstName,
        (c.properties as Record<string, unknown>)?.lastName
      ].filter(Boolean).join(' ')
    ),
    companies: (Array.isArray(eng.accounts) ? eng.accounts : []).map((a: Record<string, unknown>) =>
      (a.properties as Record<string, unknown>)?.name
    ).filter(Boolean),
    from: (eng.fromEmail as string) || '',
    preview: (eng.bodyPreview as string) || '',
    hasAiSummary: eng.hasAiSummary as boolean,
    hasAiBrief: eng.hasAiBrief as boolean,
    hasTranscript: eng.hasTranscript as boolean,
    contacts: eng.contacts,
    accounts: eng.accounts,
    displayName: (eng.displayName as string) || '',
    description: (eng.description as string) || '',
    startTime: (eng.startTime as string) || '',
    metadataFrom: eng.metadataFrom,
    metadata: eng.metadata,
    endTime: (eng.endTime as string) || '',
    link: (eng.link as string) || '',
  };
}

export interface UseEngagementsBaseOptions {
  /** The entity-specific filter fields (e.g. { contactId: '123' }) */
  entityFilter: Record<string, unknown>;
  /** Entity ID for resetting load state when it changes */
  entityId: string;
  type?: DdbEngagementType;
  pageSize?: number;
  skip?: boolean;
  /** Dummy meetings data for preview mode */
  dummyMeetings: unknown[];
  /** Dummy emails data for preview mode */
  dummyEmails: unknown[];
  /** Dummy calls data for preview mode */
  dummyCalls?: unknown[];
}

/**
 * Shared hook for fetching engagements (meetings/emails) associated with any entity.
 * Supports pagination and Lovable Preview mode.
 */
export function useEngagementsBase({
  entityFilter,
  entityId,
  type,
  pageSize = 20,
  skip = false,
  dummyMeetings,
  dummyEmails,
  dummyCalls = [],
}: UseEngagementsBaseOptions) {
  const workspaceId = useWorkspaceId();
  const client = useApolloClient();
  const [hasMore, setHasMore] = useState(true);
  const [allEngagements, setAllEngagements] = useState<unknown[]>([]);
  const [getEngagements, { data, loading, error, fetchMore }] = useGetEngagementsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const initialLoadRef = useRef(false);

  const filter: EngagementFilter = useMemo(
    () => ({
      workspaceId: workspaceId || '',
      ...entityFilter,
      deleted: false,
      ...(type && { engagementType: type }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, entityId, type]
  );

  // Reset initialLoadRef when entity changes
  useEffect(() => {
    initialLoadRef.current = false;
  }, [entityId]);

  // Initial load
  useEffect(() => {
    if (!skip && !isLovablePreview() && workspaceId && entityId && !initialLoadRef.current) {
      initialLoadRef.current = true;
      getEngagements({
        variables: {
          filter,
          first: pageSize,
          sort: [{ field: 'startTime', sort: 'DESC' }],
        },
      });
    }
  }, [skip, workspaceId, entityId, filter, pageSize, getEngagements, type]);

  // Evict this entity's accumulated pagination data on unmount, and ONLY this
  // entity's. `evict({ fieldName: 'engagements' })` drops every engagements entry
  // in the cache, including ones this hook never wrote — the meetings list behind
  // a detail sheet, for instance. That list watches its query with
  // cache-and-network, so the wipe made it refetch page 1 and collapse from
  // however many pages the user had scrolled back down to the first fifteen rows.
  // Closing a meeting sheet unmounts the PersonDetailSheet/CompanyDetailSheet it
  // renders, which is what fired this.
  // Deferred to avoid blocking navigation — see use-companies.ts for details.
  useEffect(() => {
    if (!entityId) return;
    return () => {
      setTimeout(() => {
        client.cache.modify({
          id: 'ROOT_QUERY',
          fields: {
            engagements: (existing, { storeFieldName, DELETE }) =>
              // storeFieldName carries the serialized args, so the entity id in
              // this hook's filter identifies the entries it is responsible for.
              storeFieldName.includes(entityId) ? DELETE : existing,
          },
        });
        client.cache.gc();
      }, 0);
    };
  }, [client, entityId]);

  // Update engagements when data changes
  useEffect(() => {
    if (data?.engagements?.edges) {
      const newEngagements = data.engagements.edges.map((edge) => edge.node);
      setAllEngagements(newEngagements);
      setHasMore(data.engagements.pageInfo?.hasNextPage || false);
    }
  }, [data, entityId, type]);

  const loadMore = useCallback(() => {
    if (!fetchMore || !data?.engagements?.pageInfo?.endCursor || !hasMore) {
      return;
    }

    fetchMore({
      variables: {
        after: data.engagements.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...fetchMoreResult,
          engagements: {
            ...fetchMoreResult.engagements,
            edges: [
              ...(prev.engagements?.edges || []),
              ...(fetchMoreResult.engagements?.edges || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, data, hasMore]);

  const engagements = useMemo(() => {
    if (isLovablePreview()) {
      if (type === DdbEngagementType.Meeting) return dummyMeetings;
      if (type === DdbEngagementType.Call) return dummyCalls;
      return dummyEmails;
    }
    return allEngagements.map((eng) => transformEngagement(eng as Record<string, unknown>));
  }, [allEngagements, type, dummyMeetings, dummyEmails, dummyCalls]);

  return {
    engagements,
    loading: isLovablePreview() ? false : loading,
    error: isLovablePreview() ? null : error,
    hasMore: isLovablePreview() ? false : hasMore,
    loadMore: isLovablePreview() ? () => {} : loadMore,
  };
}
